import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { scrollToId } from "../lib/site";

const LINKS = [
  { id: "tentang", label: "Tentang Kami" },
  { id: "layanan", label: "Layanan" },
  { id: "produk", label: "Produk" },
  { id: "keunggulan", label: "Keunggulan" },
  { id: "portofolio", label: "Portofolio" },
  { id: "faq", label: "FAQ" },
  { id: "kontak", label: "Kontak" },
];

export function Navbar() {
  const { content, wa } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <header
      data-testid="nav-container"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled || open ? "bg-navy-950/85 backdrop-blur-xl border-b border-glacier/20" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2.5 group">
          <span className="bg-white rounded-sm px-2 py-1.5 flex items-center shadow-[0_0_20px_-4px_rgba(0,240,255,0.35)]">
            <img src="/logo-mark.png" alt="Logo SolusiCold" className="h-7 w-auto" />
          </span>
          <span className="font-display text-xl font-bold tracking-wide uppercase"><span className="text-frost">Solusi</span><span className="text-ice">Cold</span></span>
        </button>

        <ul className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <li key={l.id}>
              <button
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-sm text-steel hover:text-ice transition-colors duration-200 font-medium"
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-cta-konsultasi"
            href={wa()}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-2 bg-ice text-navy-950 font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-glacier transition-colors duration-200"
          >
            <MessageCircle className="w-4 h-4" />
            Konsultasi Gratis
          </a>
          <button
            data-testid="nav-hamburger"
            onClick={() => setOpen(!open)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-frost"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-navy-950/95 backdrop-blur-xl border-b border-glacier/20"
          >
            <ul className="px-6 py-6 space-y-1">
              {LINKS.map((l, i) => (
                <motion.li key={l.id} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <button
                    data-testid={`nav-mobile-link-${l.id}`}
                    onClick={() => go(l.id)}
                    className="block w-full text-left py-3 font-display text-2xl font-semibold uppercase text-frost hover:text-ice transition-colors"
                  >
                    {l.label}
                  </button>
                </motion.li>
              ))}
              <li className="pt-4">
                <a
                  data-testid="nav-mobile-cta"
                  href={wa()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-ice text-navy-950 font-semibold px-5 py-3.5 rounded-sm"
                >
                  <MessageCircle className="w-4 h-4" />
                  Konsultasi Gratis
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
